import {
	EntityStatus,
	FeedTank,
	Product,
	ProductStatus,
	ProductType,
	SimproJobDetails,
} from '@/api';
import { Constants } from '@/common/Constants';
import { Entity } from '@/models/entity';
import Vue from 'vue';

export class ProductStatusService<T extends Entity> {
	constructor(private product: Product & { simproJobs?: SimproJobDetails[] }) {}

	public get pendingEntities(): boolean {
		return this.entities.some((entity: T) => entity.status === EntityStatus.Pending);
	}

	public get draftEntities(): boolean {
		return this.entities.some((entity: T) => entity.status === EntityStatus.Draft);
	}

	public get draftOrPendingEntities(): boolean {
		return this.entities.some(
			(entity: T) => entity.status === EntityStatus.Draft || entity.status === EntityStatus.Pending,
		);
	}

	public get canCreateInstallJobs(): boolean {
		return this.pendingEntities;
	}

	public get eligibleForRepair(): boolean {
		return (
			!!this.product.faultStatus ||
			(this.product.status !== ProductStatus.Removed &&
				this.product.status !== ProductStatus.Cancelled)
		);
	}

	public get liveEntities(): boolean {
		return this.entities.some(
			(entity: T) => entity.status === EntityStatus.Live || entity.status === EntityStatus.SignOff,
		);
	}

	private get entities(): T[] {
		switch (this.product.type) {
			case ProductType.Milk: {
				return (this.product.vats ?? []) as T[];
			}
			case ProductType.Fuel: {
				return (this.product.tanks ?? []) as T[];
			}
			case ProductType.Water: {
				return [
					...(this.product.watermeters ?? []),
					...(this.product.watertanks ?? []),
					...(this.product.watermonitors ?? []),
				] as T[];
			}
			case ProductType.Feed: {
				return (this.product.feedtanks ?? []) as T[];
			}
			default: {
				return [];
			}
		}
	}

	public getEntitiesTabTooltip(entityType: string, entities: T[]): string | undefined {
		const isDraft = entities?.some((t: T) => t.status === EntityStatus.Draft);
		const isPending = entities?.some((t: T) => t.status === EntityStatus.Pending);
		const tooltipStatuses: string[] = [];
		if (isDraft) tooltipStatuses.push('draft');
		if (isPending) tooltipStatuses.push('pending');
		return this.hasEntitiesTabTooltip(entities)
			? `Has ${entityType} in ${tooltipStatuses.join(', ').replace(/, ([^,]*)$/, ' and $1')} status`
			: undefined;
	}

	public hasEntitiesTabTooltip(entities: T[]): boolean {
		return entities?.some(this.hasEntityTooltip) ?? false;
	}

	private hasEntityTooltip(entity: T): boolean {
		return entity?.status === EntityStatus.Draft || entity?.status === EntityStatus.Pending;
	}

	public static formatProductStatus(status: string | null, termsState: string): string {
		if (!status) {
			return '';
		}
		if (status === Constants.ENTITY_STATE_PENDING && termsState === 'pendingNewTermsSignage') {
			return 'Awaiting Terms & Conditions';
		}
		return Vue.filter('titleCase')(status);
	}
}
